import { useEffect, useState } from "react";
import { useAssignDay, type Proposal } from "../../api/routing";

/** Who works a proposed day, and on which date. */
export interface DayChoice {
  providerId: string;
  date: string;
}

const keyOf = (choice: DayChoice | undefined) => (choice ? `${choice.providerId}|${choice.date}` : "");
const complete = (choice: DayChoice | undefined): choice is DayChoice => !!choice && choice.providerId !== "" && choice.date !== "";

/** The crew pickers on the day cards. A crew can be chosen before the plan is
 * saved; saving sends every complete choice, and once saved each change is
 * sent as it is made. "Assigned" means the server holds that exact crew and
 * date for the saved plan, which is what accepting requires of every day. */
export function useDayCrews(proposal: Proposal | null, savedId: number | null, defaultProviderId: string) {
  const assign = useAssignDay();
  const [choices, setChoices] = useState<Record<number, DayChoice>>({});
  const [held, setHeld] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [pending, setPending] = useState<readonly number[]>([]);

  // A new proposal starts every day on the default crew and its proposed date.
  useEffect(() => {
    setChoices(proposal ? Object.fromEntries(proposal.days.map((d) => [d.day_index, { providerId: defaultProviderId, date: d.date ?? "" }])) : {});
    setHeld({});
    setErrors({});
  }, [proposal, defaultProviderId]);

  async function send(planId: number, dayIndex: number, choice: DayChoice) {
    setPending((p) => [...p, dayIndex]);
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => Number(k) !== dayIndex)));
    try {
      await assign.mutateAsync({ planId, dayIndex, provider_id: Number(choice.providerId), date: choice.date });
      setHeld((h) => ({ ...h, [dayIndex]: keyOf(choice) }));
    } catch (error) {
      setErrors((e) => ({ ...e, [dayIndex]: error instanceof Error ? error.message : "The day could not be assigned." }));
    } finally {
      setPending((p) => p.filter((d) => d !== dayIndex));
    }
  }

  function choose(dayIndex: number, choice: DayChoice) {
    setChoices((c) => ({ ...c, [dayIndex]: choice }));
    if (savedId !== null && complete(choice)) {
      void send(savedId, dayIndex, choice);
    }
  }

  /** After a save: every day that already has a crew and a date, one by one. */
  async function sendAll(planId: number) {
    for (const [index, choice] of Object.entries(choices)) {
      if (complete(choice)) {
        await send(planId, Number(index), choice);
      }
    }
  }

  const days = proposal?.days ?? [];
  const isAssigned = (dayIndex: number) => savedId !== null && held[dayIndex] !== undefined && held[dayIndex] === keyOf(choices[dayIndex]);
  const assignedCount = days.filter((d) => isAssigned(d.day_index)).length;
  return {
    choices,
    choose,
    sendAll,
    isAssigned,
    isPending: (dayIndex: number) => pending.includes(dayIndex),
    errors,
    assignedCount,
    allAssigned: days.length > 0 && assignedCount === days.length,
  };
}

export type DayCrews = ReturnType<typeof useDayCrews>;
